/**
 * The designer's own view state — zoom, guides, theme.
 *
 * None of it is print data. It lives here, beside the commands, only so the
 * widget and Studio agree on one shape and one validator: a saved workspace
 * preference must survive a round trip through storage without ever being
 * mistaken for part of the answer.
 */

/** Editor-only preferences never enter a print answer. */
export interface DesignerViewSettings {
  zoom: number;
  panMode: boolean;
  guidelines: boolean;
  grid: boolean;
  rulers: boolean;
  snap: boolean;
  lockAspect: boolean;
  transparency: boolean;
  miniToolbar: boolean;
  darkMode: boolean;
  review: boolean;
}

export const DESIGNER_VIEW_DEFAULTS: DesignerViewSettings = {
  zoom: 1,
  panMode: false,
  guidelines: true,
  grid: false,
  rulers: true,
  snap: true,
  lockAspect: false,
  transparency: false,
  miniToolbar: true,
  darkMode: false,
  review: false,
};

/** Zoom is the only non-boolean, and the only one with a range. */
export const DESIGNER_ZOOM_RANGE = { min: 0.25, max: 4 } as const;

/**
 * Reads a stored or posted view, refusing anything that is not exactly this
 * shape. Strict on purpose: an unknown key here usually means a caller is
 * trying to smuggle design data through the preferences door.
 */
export function parseDesignerView(raw: unknown): DesignerViewSettings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Invalid canvas settings');
  const view = { ...DESIGNER_VIEW_DEFAULTS, ...raw } as DesignerViewSettings;
  const known = Object.keys(raw).every((key) => key in DESIGNER_VIEW_DEFAULTS);
  const zoomOk = typeof view.zoom === 'number' && Number.isFinite(view.zoom)
    && view.zoom >= DESIGNER_ZOOM_RANGE.min && view.zoom <= DESIGNER_ZOOM_RANGE.max;
  const togglesOk = (Object.keys(DESIGNER_VIEW_DEFAULTS) as Array<keyof DesignerViewSettings>)
    .filter((key) => key !== 'zoom')
    .every((key) => typeof view[key] === 'boolean');
  if (!known || !zoomOk || !togglesOk) throw new Error('Invalid canvas settings');
  return view;
}
